import React, {useState, useEffect} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {OfferSubProduct} from '../../../types/offerProduct.type';
import {axiosCaller} from '../../../utils/apiCaller';
import {toast} from 'react-toastify';
import {formatPrice} from '../../../utils/react';
import {ArticleInfomation} from '../../../types/ArticleInformation.type';
import FontAwesome from 'react-fontawesome';
import SubProductRow from './SubProductRow';

interface Props {
    productId: number;
    productIndex: number;
    parentDiscount: number;
    articles: ArticleInfomation[];
    onSubProductsChange: (subProducts: OfferSubProduct[]) => void;
    permissions: any;
    readOnly?: boolean;
}

const SubProductSection: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [subProducts, setSubProducts] = useState<OfferSubProduct[]>([]);
    const [collapsed, setCollapsed] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (props.productId) {
            fetchSubProducts();
        }
    }, [props.productId]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        props.onSubProductsChange(subProducts);
    }, [subProducts]);

    const fetchSubProducts = async () => {
        if (!props.productId) return;
        setLoading(true);
        try {
            const res = await axiosCaller('GET', `products/${props.productId}/sub-products`);
            const data = res?.data?.data || [];
            setSubProducts(data);
        } catch (e) {
            console.error('Error fetching sub-products:', e);
        } finally {
            setLoading(false);
        }
    };

    const addEmptySubProduct = () => {
        const newSubProduct: OfferSubProduct = {
            name: '',
            description: '',
            quantity: 1,
            costPerUnit: 0,
            coefficient: 1,
            effectivePrice: 0,
            tax: 0,
            discount: 0,
            totalPrice: 0,
            sortOrder: subProducts.length + 1,
        };
        setSubProducts(prev => [...prev, newSubProduct]);
        setCollapsed(false);
    };

    const handleSave = async (index: number, data: OfferSubProduct) => {
        try {
            if (data.id) {
                const res = await axiosCaller('PATCH', `products/sub-products/${data.id}`, {
                    name: data.name,
                    description: data.description,
                    articleId: data.articleId,
                    unitName: data.unitName,
                    quantity: data.quantity,
                    costPerUnit: data.costPerUnit,
                    coefficient: data.coefficient,
                    effectivePrice: data.effectivePrice,
                    tax: data.tax,
                    discount: data.discount,
                });
                const updated = res?.data?.data;
                if (updated) {
                    setSubProducts(prev => prev.map((sp, i) => i === index ? updated : sp));
                    toast.success(fm('offer.subProduct.toast.saved', 'Sub-product saved!'));
                }
            } else {
                const res = await axiosCaller('POST', `products/${props.productId}/sub-products`, {
                    name: data.name,
                    description: data.description,
                    articleId: data.articleId,
                    unitName: data.unitName,
                    quantity: data.quantity,
                    costPerUnit: data.costPerUnit,
                    coefficient: data.coefficient,
                    effectivePrice: data.effectivePrice,
                    tax: data.tax,
                    discount: data.discount,
                });
                const created = res?.data?.data;
                if (created) {
                    setSubProducts(prev => prev.map((sp, i) => i === index ? created : sp));
                    toast.success(fm('offer.subProduct.toast.created', 'Sub-product created!'));
                }
            }
        } catch (e) {
            toast.error(fm('offer.subProduct.toast.saveFailed', 'Sub-product save failed!'));
        }
    };

    const handleDelete = async (index: number) => {
        const sp = subProducts[index];
        if (sp.id) {
            try {
                await axiosCaller('DELETE', `products/sub-products/${sp.id}`);
                toast.success(fm('offer.subProduct.toast.deleted', 'Sub-product deleted!'));
            } catch (e) {
                toast.error(fm('offer.subProduct.toast.deleteFailed', 'Delete failed!'));
                return;
            }
        }
        setSubProducts(prev => prev.filter((_, i) => i !== index));
    };

    const handleMoveUp = async (index: number) => {
        if (index === 0) return;
        const newList = [...subProducts];
        [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
        const updated = newList.map((sp, i) => ({...sp, sortOrder: i + 1}));
        setSubProducts(updated);
        await persistOrder(updated);
    };

    const handleMoveDown = async (index: number) => {
        if (index === subProducts.length - 1) return;
        const newList = [...subProducts];
        [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
        const updated = newList.map((sp, i) => ({...sp, sortOrder: i + 1}));
        setSubProducts(updated);
        await persistOrder(updated);
    };

    const persistOrder = async (list: OfferSubProduct[]) => {
        const savedItems = list.filter(sp => sp.id);
        if (savedItems.length < 2) return;
        try {
            await axiosCaller('PATCH', `products/${props.productId}/sub-products/reorder`, {
                items: savedItems.map(sp => ({id: sp.id, sortOrder: sp.sortOrder}))
            });
        } catch (e) {
            toast.error(fm('offer.subProduct.toast.reorderFailed', 'Reorder failed!'));
        }
    };

    const subProductsTotal = subProducts.reduce((sum, sp) => sum + (sp.totalPrice || 0), 0);
    const hasSubProducts = subProducts.length > 0;

    if (!props.productId) return null;

    return (
        <div className="sp-section">
            {/* Header */}
            <div className="sp-section-header" onClick={() => hasSubProducts && setCollapsed(c => !c)}>
                <div className="sp-section-header-left">
                    {hasSubProducts && (
                        <FontAwesome
                            name="chevron-down"
                            className={`sp-section-chevron ${collapsed ? 'is-collapsed' : ''}`}
                        />
                    )}
                    <span className="sp-section-title">
                        <FormattedMessage id="offer.subProduct.title" defaultMessage="Sub-products"/>
                        {hasSubProducts && <span className="sp-section-count"> ({subProducts.length})</span>}
                    </span>
                    {hasSubProducts && collapsed && (
                        <span className="sp-section-collapsed-total">— {formatPrice(subProductsTotal)} €</span>
                    )}
                </div>
                {!props.readOnly && (
                    <button
                        type="button"
                        className="sp-add-btn"
                        onClick={(e) => { e.stopPropagation(); addEmptySubProduct(); }}
                    >
                        + {fm('offer.subProduct.add', 'Add sub-product')}
                    </button>
                )}
            </div>

            {/* Table body */}
            {!collapsed && hasSubProducts && (
                <div className="sp-table">
                    {/* Column headers — shown once */}
                    <div className="sp-table-header">
                        <div className="sp-col sp-col-pos"><FormattedMessage id="offer.subProduct.col.pos" defaultMessage="Pos." /></div>
                        <div className="sp-col sp-col-name"><FormattedMessage id="offer.subProduct.col.name" defaultMessage="Name" /></div>
                        <div className="sp-col sp-col-type"><FormattedMessage id="offer.subProduct.col.unit" defaultMessage="Unit" /></div>
                        <div className="sp-col sp-col-coeff"><FormattedMessage id="offer.subProduct.col.coefficient" defaultMessage="Coef." /></div>
                        <div className="sp-col sp-col-qty"><FormattedMessage id="offer.subProduct.col.quantity" defaultMessage="Quantity" /></div>
                        <div className="sp-col sp-col-cost"><FormattedMessage id="offer.subProduct.col.cost" defaultMessage="Cost" /></div>
                        <div className="sp-col sp-col-price"><FormattedMessage id="offer.subProduct.col.price" defaultMessage="Price" /></div>
                        <div className="sp-col sp-col-tax"><FormattedMessage id="offer.subProduct.col.tax" defaultMessage="VAT" /></div>
                        <div className="sp-col sp-col-disc"><FormattedMessage id="offer.subProduct.col.discount" defaultMessage="Disc.%" /></div>
                        <div className="sp-col sp-col-total"><FormattedMessage id="offer.subProduct.col.total" defaultMessage="Total" /></div>
                        <div className="sp-col sp-col-actions"></div>
                    </div>

                    {/* Rows */}
                    {subProducts.map((sp, index) => (
                        <SubProductRow
                            key={sp.id || `new-${index}`}
                            data={sp}
                            index={index}
                            positionPrefix={`${props.productIndex + 1}.${index + 1}`}
                            parentDiscount={props.parentDiscount}
                            articles={props.articles}
                            permissions={props.permissions}
                            readOnly={props.readOnly}
                            onSave={(data) => handleSave(index, data)}
                            onDelete={() => handleDelete(index)}
                            onMoveUp={index > 0 ? () => handleMoveUp(index) : undefined}
                            onMoveDown={index < subProducts.length - 1 ? () => handleMoveDown(index) : undefined}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SubProductSection;
